import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePerformanceReviewReopen } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { ApiPerformanceReview } from "@/types/performance-review.types.ts";
import { Button, Modal, ModalProps, Stack, Text, useModals } from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const ReopenSchemaObject = yup.object({
  date_ends_at: yup.date().defined().nullable(),
});

type ReopenSchema = yup.InferType<typeof ReopenSchemaObject>;

export interface PerformanceReviewReopenModalProps extends ModalProps {
  review: ApiPerformanceReview;
  onSuccess?: () => void;
}

export function PerformanceReviewReopenModal({
  review,
  onSuccess,
  ...props
}: PerformanceReviewReopenModalProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();

  const reopenReview = usePerformanceReviewReopen();

  const today = new Date();
  const minDate =
    review.date_starts_at && review.date_starts_at > today ? review.date_starts_at : today;

  const defaultDateEndsAt = (() => {
    if (!review.date_ends_at) return null;
    const nextWeekDate = new Date();
    nextWeekDate.setDate(nextWeekDate.getDate() + 7);
    return nextWeekDate > minDate ? nextWeekDate : minDate;
  })();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReopenSchema>({
    resolver: yupResolver(ReopenSchemaObject),
    defaultValues: { date_ends_at: defaultDateEndsAt },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await reopenReview.mutateAsync({
        id: review.id,
        payload: { date_ends_at: data.date_ends_at },
      });
      onSuccess?.();
      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t("performance_review:reopen_review_question")}
      withCloser
      actions={
        <Stack gap={"smd"} direction={"horizontal"} justify={"between"}>
          <ServerErrorField errors={errors} />
          <Button
            className={"w-full"}
            type={"submit"}
            size={"lg"}
            onClick={onSubmit}
            isLoading={reopenReview.isPending}
          >
            {t("performance_review:reopen_review")}
          </Button>
          <Button
            onClick={() => {
              close(id);
            }}
            variant={"secondary"}
            size={"lg"}
            className={"w-full"}
          >
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"lg"}>
        <Text className={"text-sm"}>{t("performance_review:reopen_review_confirm")}</Text>
        <Controller
          control={control}
          name="date_ends_at"
          render={({ field: { value, onChange } }) => (
            <Datepicker
              value={value}
              onChange={onChange}
              label={t("performance_review:review_date_ends_at")}
              isClearable={true}
              minDate={minDate}
              error={errors.date_ends_at?.message}
            />
          )}
        />
      </Stack>
    </Modal>
  );
}
